// Persisted user preferences for TTS voice + playback quality.
//
// Two prefs live here:
//   tiplingo_tts_voice_v1     -> "female" (nova) | "male" (onyx)
//   tiplingo_tts_playback_v1  -> "premium" (OpenAI, default) | "fast" (native first)
//
// Why two prefs?
//   - GENDER is what the user picks consciously ("I want a male voice").
//   - QUALITY locks the engine that produces the sound. PREMIUM forces every
//     playback through OpenAI's onyx/nova (sounds identical on every device,
//     never grabs an iOS "compact" novelty voice). FAST uses the device's
//     native voice for instant playback (no network roundtrip) and only
//     falls back to OpenAI when native fails.
//
// We default QUALITY to "premium" because the user reported that iOS picked
// a creepy lo-fi compact voice ("Bruce/Fred/Junior/Ralph") for English Male
// playback — premium completely eliminates that risk. Travelers on flaky
// cellular can still opt in to "fast" if they prefer instant playback.

const VOICE_KEY = "tiplingo_tts_voice_v1";
const PLAYBACK_KEY = "tiplingo_tts_playback_v1";

export const TTS_VOICE_OPTIONS = [
  {
    value: "female",
    label: "Female",
    description: "Soft, friendly tone (Nova).",
    openaiVoice: "nova",
  },
  {
    value: "male",
    label: "Male",
    description: "Deeper, lower pitch (Onyx).",
    openaiVoice: "onyx",
  },
];

export const TTS_PLAYBACK_OPTIONS = [
  {
    value: "premium",
    label: "Premium",
    description: "Studio-quality voice (recommended). Same on every device.",
  },
  {
    value: "fast",
    label: "Fast",
    description: "Instant on-device voice. Quality varies by language.",
  },
];

export function getTtsVoice() {
  try {
    const raw = localStorage.getItem(VOICE_KEY);
    if (raw === "female" || raw === "male") return raw;
  } catch (e) {
    console.error("getTtsVoice: localStorage unavailable", e);
  }
  return "female";
}

export function getTtsPlaybackMode() {
  try {
    const raw = localStorage.getItem(PLAYBACK_KEY);
    if (raw === "premium" || raw === "fast") return raw;
  } catch (e) {
    console.error("getTtsPlaybackMode: localStorage unavailable", e);
  }
  return "premium";
}

export function clearLocalTtsVoice() {
  try {
    localStorage.removeItem(VOICE_KEY);
    localStorage.removeItem(PLAYBACK_KEY);
  } catch (e) {
    console.error("clearLocalTtsVoice: localStorage unavailable", e);
  }
}

// Module-level listeners notified whenever the user toggles voice gender
// OR playback quality.
const voiceChangeListeners = new Set();

export function subscribeTtsVoiceChange(callback) {
  if (typeof callback !== "function") return () => {};
  voiceChangeListeners.add(callback);
  return () => voiceChangeListeners.delete(callback);
}

function notifyVoiceChange(newValue, previous) {
  voiceChangeListeners.forEach((cb) => {
    try { cb(newValue, previous); } catch (e) { console.error("voice listener threw", e); }
  });
}

export function setTtsVoice(value) {
  if (value !== "female" && value !== "male") return;
  const previous = getTtsVoice();
  try {
    localStorage.setItem(VOICE_KEY, value);
  } catch (e) {
    console.error("setTtsVoice: localStorage unavailable", e);
  }
  if (previous !== value) notifyVoiceChange(value, previous);
}

export function setTtsPlaybackMode(value) {
  if (value !== "premium" && value !== "fast") return;
  try {
    localStorage.setItem(PLAYBACK_KEY, value);
  } catch (e) {
    console.error("setTtsPlaybackMode: localStorage unavailable", e);
  }
}

export function getOpenAIVoiceId() {
  const choice = getTtsVoice();
  const match = TTS_VOICE_OPTIONS.find((o) => o.value === choice);
  return match ? match.openaiVoice : "nova";
}

// ---------------------------------------------------------------------
// Native (device-side) voice picker.
//
// On iOS / macOS the SpeechSynthesis API exposes ~150 voices including
// novelty ones from the original macOS Speech Recognition era: Bad News,
// Bruce, Fred, Junior, Ralph, Albert, Whisper, Pipe Organ, Cellos, etc.
// Many of these sound "lo-fi" or "creepy" — a traveler tapping speaker
// hears what they perceive as a dying-old-man voice. We aggressively
// BLOCKLIST these so the picker can never land on one.
//
// On top of the blocklist, a PREFERRED_VOICES table per (language,
// gender) hard-prioritises the modern, neural-quality voices Apple
// ships today (Samantha / Aaron / Daniel / Oliver / Karen / Victoria /
// Kyoko / Otoya / etc.). This guarantees deterministic results across
// iOS versions.
// ---------------------------------------------------------------------

// Names matched here are NEVER returned by pickVoiceForLang. Includes
// every Apple novelty voice + the lo-fi compact voices that triggered
// the original "creepy old man" complaint.
const NOVELTY_VOICE_BLOCKLIST = new Set([
  "bad news", "bahh", "bells", "boing", "bubbles", "cellos", "deranged",
  "good news", "hysterical", "jester", "organ", "pipe organ", "trinoids",
  "whisper", "wobble", "zarvox",
  // Lo-fi compact voices (sound "dying" on modern iOS):
  "albert", "bruce", "fred", "junior", "kathy", "princess", "ralph",
  "vicki", "agnes", "alva (compact)",
]);

// Hand-curated preference order for the top travel languages. The picker
// walks this list in order and returns the FIRST installed voice that
// matches. Any voice not in this list is still considered as a fallback
// via the gender registry below, but only after preferred picks are
// exhausted.
const PREFERRED_VOICES = {
  en: {
    male:   ["Aaron", "Daniel", "Tom", "Alex", "Oliver", "Arthur", "Rishi"],
    female: ["Samantha", "Ava", "Karen", "Susan", "Allison", "Victoria", "Serena", "Tessa"],
  },
  es: {
    male:   ["Jorge", "Diego", "Juan", "Carlos"],
    female: ["Mónica", "Monica", "Paulina", "Esperanza", "Marisol"],
  },
  fr: {
    male:   ["Thomas", "Yannick"],
    female: ["Amélie", "Amelie", "Marie", "Aurélie", "Audrey"],
  },
  de: {
    male:   ["Markus", "Martin", "Yannick"],
    female: ["Anna", "Petra", "Helena"],
  },
  it: {
    male:   ["Luca"],
    female: ["Alice", "Silvia", "Paola", "Federica"],
  },
  pt: {
    male:   ["Felipe"],
    female: ["Luciana", "Joana", "Catarina"],
  },
  ja: {
    male:   ["Otoya", "Hattori"],
    female: ["Kyoko"],
  },
  ko: {
    male:   ["Minsu"],
    female: ["Yuna", "Sora"],
  },
  zh: {
    male:   ["Li-mu", "Lee"],
    female: ["Tian-Tian", "Ting-Ting", "Sin-Ji", "Mei-Jia"],
  },
  ru: {
    male:   ["Yuri"],
    female: ["Milena"],
  },
  ar: {
    male:   ["Maged", "Tarik"],
    female: ["Majed"],
  },
  hi: {
    male:   ["Rishi"],
    female: ["Lekha", "Veena"],
  },
  nl: { male: ["Xander"], female: ["Ellen"] },
  pl: { male: ["Krzysztof"], female: ["Zosia", "Ewa"] },
  sv: { male: ["Oskar"], female: ["Alva"] },
  da: { male: ["Magnus"], female: ["Sara"] },
  no: { male: ["Henrik"], female: ["Nora"] },
  fi: { male: ["Mikko"], female: ["Satu"] },
  tr: { male: ["Cem"], female: ["Yelda"] },
  el: { male: ["Nikos"], female: ["Melina"] },
  cs: { male: ["Krzysztof"], female: ["Zuzana"] },
  ro: { female: ["Ioana"], male: [] },
  th: { female: ["Kanya"], male: [] },
  vi: { female: ["Linh"], male: [] },
};

// Fallback gender registry for voices not in PREFERRED_VOICES. Used only
// when none of the preferred picks are installed on the device.
const VOICE_GENDER_REGISTRY = {
  // English (modern)
  aaron: "male", alex: "male", daniel: "male", tom: "male", oliver: "male",
  arthur: "male", rishi: "male", samantha: "female", ava: "female",
  karen: "female", susan: "female", allison: "female", victoria: "female",
  serena: "female", tessa: "female", moira: "female", fiona: "female",
  kate: "female", zoe: "female", joelle: "female", catherine: "female",
  // Japanese
  kyoko: "female", otoya: "male", hattori: "male",
  // Korean
  yuna: "female", sora: "female", minsu: "male",
  // Chinese
  "tian-tian": "female", "ting-ting": "female", "sin-ji": "female",
  "mei-jia": "female", "li-mu": "male", lee: "male",
  // Spanish
  monica: "female", "mónica": "female", paulina: "female",
  esperanza: "female", marisol: "female", jorge: "male", diego: "male",
  juan: "male", carlos: "male",
  // French
  amelie: "female", "amélie": "female", marie: "female", thomas: "male",
  aurelie: "female", "aurélie": "female", audrey: "female", yannick: "male",
  // German
  anna: "female", markus: "male", petra: "female", martin: "male",
  helena: "female",
  // Italian
  alice: "female", luca: "male", silvia: "female", paola: "female",
  federica: "female",
  // Portuguese
  luciana: "female", joana: "female", felipe: "male", catarina: "female",
  // Russian
  milena: "female", yuri: "male",
  // Arabic
  maged: "male", tarik: "male",
  // Hindi
  lekha: "female", veena: "female",
  // European
  ellen: "female", xander: "male", zosia: "female", ewa: "female",
  krzysztof: "male", alva: "female", oskar: "male", sara: "female",
  magnus: "male", nora: "female", henrik: "male", satu: "female",
  mikko: "male", cem: "male", yelda: "female", melina: "female",
  nikos: "male", zuzana: "female", ioana: "female", kanya: "female",
  linh: "female", carmit: "female",
};

const FEMALE_HINTS = ["female", "woman", "girl"];
const MALE_HINTS = ["male", "man", "boy"];

function cleanName(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim();
}

export function isBlockedVoice(name) {
  return NOVELTY_VOICE_BLOCKLIST.has(cleanName(name));
}

export function classifyVoiceGender(name) {
  if (!name) return null;
  const cleaned = cleanName(name);
  if (NOVELTY_VOICE_BLOCKLIST.has(cleaned)) return null;
  if (VOICE_GENDER_REGISTRY[cleaned]) return VOICE_GENDER_REGISTRY[cleaned];
  const firstToken = cleaned.split(/[\s_\-]+/)[0];
  if (VOICE_GENDER_REGISTRY[firstToken]) return VOICE_GENDER_REGISTRY[firstToken];
  const lower = String(name).toLowerCase();
  for (const hint of FEMALE_HINTS) {
    if (new RegExp("\\b" + hint + "\\b", "i").test(lower)) return "female";
  }
  for (const hint of MALE_HINTS) {
    if (new RegExp("\\b" + hint + "\\b", "i").test(lower)) return "male";
  }
  return null;
}

/**
 * Pick the best SpeechSynthesisVoice from the available system voices
 * that matches both the target BCP-47 language tag AND the user's
 * gender preference.
 *
 * Priority order (each step gated to the user's gender):
 *   1. Voice name appears in PREFERRED_VOICES[lang_base][gender] (curated
 *      modern voices).
 *   2. Exact lang tag + matching gender from VOICE_GENDER_REGISTRY.
 *   3. Base-language + matching gender.
 *   4. Any voice for the language EXCLUDING blocklisted novelty voices
 *      (so we never return Bruce/Fred/Bad News/etc.).
 *
 * Returns null if NO acceptable voice exists for the language. Callers
 * MUST then fall back to backend OpenAI TTS — never the device default,
 * because the OS default could be a novelty voice on some setups.
 */
export function pickVoiceForLang(lang, preference) {
  if (
    typeof window === "undefined" ||
    typeof window.speechSynthesis === "undefined"
  ) {
    return null;
  }
  const voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) return null;

  const target = preference === "male" ? "male" : "female";
  const langLower = (lang || "").toLowerCase();
  const langBase = langLower.split("-")[0];

  // Filter out any blocklisted voice from EVERY tier.
  const safeVoices = voices.filter((v) => !isBlockedVoice(v.name));

  // Tier 1: hand-curated preferred picks per (lang, gender).
  const preferredNames = (PREFERRED_VOICES[langBase] || {})[target] || [];
  for (const name of preferredNames) {
    const want = name.toLowerCase();
    const hit = safeVoices.find((v) => {
      const c = cleanName(v.name);
      if (c !== want && !c.startsWith(want)) return false;
      const vl = (v.lang || "").toLowerCase();
      return vl === langLower || vl.startsWith(langBase + "-");
    });
    if (hit) return hit;
  }

  // Tier 2: exact lang + gender match.
  const exactGender = safeVoices.find(
    (v) =>
      (v.lang || "").toLowerCase() === langLower &&
      classifyVoiceGender(v.name) === target
  );
  if (exactGender) return exactGender;

  // Tier 3: base-language + gender match.
  const baseGender = safeVoices.find(
    (v) =>
      (v.lang || "").toLowerCase().startsWith(langBase + "-") &&
      classifyVoiceGender(v.name) === target
  );
  if (baseGender) return baseGender;

  // Tier 4: any safe voice for the language. We deliberately do NOT
  // return blocked voices even as a last resort — better to return null
  // and let the caller fetch OpenAI TTS than play a creepy compact.
  const anyForLang =
    safeVoices.find((v) => (v.lang || "").toLowerCase() === langLower) ||
    safeVoices.find((v) =>
      (v.lang || "").toLowerCase().startsWith(langBase + "-")
    );
  return anyForLang || null;
}
